import { Component, DoCheck, ElementRef, Input, OnDestroy, Optional, Self } from '@angular/core';
import { ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, NgControl } from '@angular/forms';
import { MatLegacyFormFieldControl as MatFormFieldControl } from '@angular/material/legacy-form-field';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';

@Component({
	selector: 'app-password-input',
	templateUrl: './password-input.component.html',
	styleUrls: ['./password-input.component.scss'],

	providers: [{ provide: MatFormFieldControl, useExisting: PasswordInputComponent }],
	host: {
		'[class.input-floating]': 'shouldLabelFloat',
		'[id]': 'id',
		'[attr.aria-describedby]': 'describedBy',
	},
})
export class PasswordInputComponent
	implements ControlValueAccessor, MatFormFieldControl<string>, OnDestroy, DoCheck
{
	static nextId = 0;
	showPassword = false;
	passwordForm: UntypedFormGroup;

	@Input() parentForm;
	stateChanges = new Subject<void>();
	focused = false;
	errorState = false;
	controlType = 'app-password-input';
	id = `app-password-input-${PasswordInputComponent.nextId++}`;
	describedBy = '';

	constructor(
		formBuilder: UntypedFormBuilder,
		private _focusMonitor: FocusMonitor,
		private _elementRef: ElementRef<HTMLElement>,
		@Optional() @Self() public ngControl: NgControl
	) {
		this.passwordForm = formBuilder.group({
			password: '',
		});

		_focusMonitor.monitor(_elementRef, true).subscribe((origin) => {
			if (this.focused && !origin) {
				this.onTouched();
			}
			this.focused = !!origin;
			this.stateChanges.next();
		});

		if (this.ngControl != null) {
			this.ngControl.valueAccessor = this;
		}
	}

	get empty() {
		const password = this.passwordForm.value.password;
		return password === '' || !password;
	}

	get shouldLabelFloat() {
		return this.focused || !this.empty;
	}

	private _placeholder: string;

	@Input()
	get placeholder(): string {
		return this._placeholder;
	}

	set placeholder(value: string) {
		this._placeholder = value;
		this.stateChanges.next();
	}

	private _required = false;

	@Input()
	get required(): boolean {
		return this._required;
	}

	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
		this.stateChanges.next();
	}

	private _disabled = false;

	@Input()
	get disabled(): boolean {
		return this._disabled;
	}

	set disabled(value: boolean) {
		this._disabled = coerceBooleanProperty(value);
		this._disabled ? this.passwordForm.disable() : this.passwordForm.enable();
		this.stateChanges.next();
	}

	@Input()
	get value(): string | null {
		return this.passwordForm.value.password;
	}

	set value(pass: string | null) {
		this.passwordForm.setValue({ password: pass || '' });
		this.stateChanges.next();
	}

	onChange = (_: any) => {};

	onTouched = () => {};

	ngDoCheck(): void {
		if (this.ngControl) {
			this.errorState = this.ngControl.invalid && this.ngControl.touched;
			this.stateChanges.next();
		}
	}

	ngOnDestroy() {
		this.stateChanges.complete();
		this._focusMonitor.stopMonitoring(this._elementRef);
	}

	setDescribedByIds(ids: string[]) {
		this.describedBy = ids.join(' ');
	}

	onContainerClick(event: MouseEvent) {
		if ((event.target as Element).tagName.toLowerCase() != 'input') {
			this._elementRef.nativeElement.querySelector('input')!.focus();
		}
	}

	writeValue(pass: string | null): void {
		this.value = pass;
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	_handleInput(): void {
		this.onChange(this.passwordForm.value.password);
	}

	togglePassword(show) {
		this.showPassword = !show;
	}
}
